import { classNames } from "@/utils/classnames";
import React from "react";

// ------------------------------------------------------------------
// MenuContainerV2 — popover shell that hosts menu sections + items.
// Used as the dropdown surface of SelectV2 and any future menu-like
// trigger. The container provides outer chrome (bg, border, rounded
// corners, shadow-cds-m / dark:shadow-cds-l-dark) and clips its
// contents so sections line up cleanly with the border.
//
// Content is organized into MenuSectionV2 groups. Sections are
// separated by a 1px top border on every section after the first.
// Each section owns its own 3px inner padding so items don't bleed
// to the container edge.
//
// `data-ds-v2` on the outer div is the marker that opts this subtree
// out of the legacy `.dark [role="menu"] / [role="menuitem"]` rules
// in `styles/dark-mode.css`. Those rules were written for the
// Headless-UI-era menus and would otherwise paint zinc-800 bg +
// zinc-700 hover over our V2 chrome (their `.dark [role=…]` selector
// has higher specificity than our `!`-prefixed Tailwind utilities,
// so `!important` alone won't win the fight). See the matching
// `:not([data-ds-v2], [data-ds-v2] *)` carve-outs in dark-mode.css.
// ------------------------------------------------------------------

export const MenuContainerV2 = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  function MenuContainerV2Impl({ className, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        role={"menu"}
        data-ds-v2={""}
        className={classNames(
          // No `overflow-hidden` here: MenuItemV2's submenu pops out
          // via `absolute left-full` and would be clipped by it. The
          // rounded radius still reads correctly because no child
          // currently paints a background out to the container edge.
          "rounded-cds-6",
          // Menus are often portaled to document.body, outside the
          // Layout-owned `.design-system-typography` subtree. Re-establish
          // the typography scope here so menu text always uses Inter
          // instead of inheriting the legacy Everett app shell font.
          "design-system-typography",
          "shadow-cds-m dark:shadow-cds-l-dark",
          // Dark surface uses the project's "elevated" step
          // (#15151A), which sits one +6 RGB notch above --bg-inset.
          "bg-cds-surface-default dark:bg-cds-elevated-dark",
          // Light: zinc-200 outer stroke. Dark: 8% white — blends
          // subtly with the elevated surface and matches the project's
          // dark-mode popover convention.
          "border-[1px] border-cds-surface-border dark:border-white/[0.08]",
          // Popovers should hug their content. The app-level overflow
          // rule reserves scrollbar gutters for page/table stability,
          // but that reads as phantom right padding in compact menus.
          "no-scrollbar-gutter-subtree",
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
MenuContainerV2.displayName = "MenuContainerV2";

// ------------------------------------------------------------------
// MenuSectionV2 — grouping for related menu items. Sections are
// visually separated from each other by a thin top border (zinc-200
// light / zinc-800 dark). `first:border-t-0` suppresses the divider
// on the first section in the container.
// ------------------------------------------------------------------

export const MenuSectionV2: React.FC<React.ComponentProps<"div">> = ({
  className,
  children,
  ...rest
}) => {
  return (
    <div
      role={"group"}
      className={classNames(
        "flex flex-col p-cds-3",
        // Divider: zinc-100 light / zinc-800 dark. Dark deliberately
        // breaks the usual zinc-500 rotation (which would give
        // zinc-900 = invisible on the zinc-900 container bg); the
        // next step lighter keeps the line readable.
        "border-t-[1px] first:border-t-0",
        "border-cds-surface-active dark:border-zinc-800",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
};
